import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { withStyles, createStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography, Button } from '@material-ui/core';

import { BROWSER_VARIABLE } from 'yeself-common/typings/application/ApplicationEnum';

import { updateApplicationIdentityRequest } from 'Console/data/applicationDetail/settings/SettingsAction';

import {
    selectApplicationUserDefinitionSourceType,
    selectApplicationRoleDefinitionSourceType,
    selectApplicationRoleDefinitionValuePath,
    selectApplicationUserDefinitionValuePath,
} from 'Console/data/applicationDetail/settings/SettingsSelector';

import FormInput from 'Console/components/common/form/FormInput';

import FormSelect from 'Console/common/containers/FormSelectRCFormContainer';

const NOT_SET_VALUE = 'notSet';

const styles = (theme) =>
    createStyles({
        root: {
            flexGrow: 1,
            alignItems: 'center',
            padding: '0 24px',
            fontSize: theme.typography.pxToRem(14),
        },
        title: {
            display: 'inline-block',
            'font-weight': 'bold',
            'padding-bottom': theme.typography.pxToRem(20),
        },
        message: {
            fontSize: theme.typography.pxToRem(14),
        },
        btn: {
            marginBottom: theme.typography.pxToRem(65),
            marginTop: theme.typography.pxToRem(20),
        },
        items: {
            paddingTop: theme.typography.pxToRem(20),
            paddingBottom: theme.typography.pxToRem(20),
            [theme.breakpoints.down('lg')]: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
            },
        },
        smallText: {
            fontSize: theme.typography.pxToRem(14),
            paddingRight: theme.spacing.unit * 3,
            paddingLeft: theme.spacing.unit * 3,
            [theme.breakpoints.down('lg')]: {
                paddingTop: theme.spacing.unit * 3,
                paddingBottom: theme.spacing.unit,
                paddingLeft: 0,
                paddingRight: 0,
            },
        },
        formInput: {
            width: '33%',
            [theme.breakpoints.between('xs', 'sm')]: {
                width: '100%',
            },
        },
        formInputLarge: {
            width: '100%',
            [theme.breakpoints.between('xs', 'sm')]: {
                width: '100%',
            },
        },
    });

const IDENTITY_USER_OPTIONS = [
    {
        value: NOT_SET_VALUE,
        name: <FormattedMessage id="Settings.user.role.notSet" defaultMessage="(Not set)" />,
    },
    {
        value: BROWSER_VARIABLE.COOKIE,
        name: (
            <FormattedMessage
                id={`Settings.user.role.${BROWSER_VARIABLE.COOKIE}`}
                defaultMessage="Cookie"
            />
        ),
    },
    {
        value: BROWSER_VARIABLE.LOCAL_STORAGE,
        name: (
            <FormattedMessage
                id={`Settings.user.role.${BROWSER_VARIABLE.LOCAL_STORAGE}`}
                defaultMessage="Local Storage"
            />
        ),
    },
    {
        value: BROWSER_VARIABLE.JS_VARIABLE,
        name: (
            <FormattedMessage
                id={`Settings.user.role.${BROWSER_VARIABLE.JS_VARIABLE}`}
                defaultMessage="JavaScript Variable"
            />
        ),
    },
    {
        value: BROWSER_VARIABLE.CSS_PATH,
        name: (
            <FormattedMessage
                id={`Settings.user.role.${BROWSER_VARIABLE.CSS_PATH}`}
                defaultMessage="CSS Selector"
            />
        ),
    },
    {
        value: BROWSER_VARIABLE.URL_QUERY,
        name: (
            <FormattedMessage
                id={`Settings.user.role.${BROWSER_VARIABLE.URL_QUERY}`}
                defaultMessage="URL Query"
            />
        ),
    },
    {
        value: BROWSER_VARIABLE.SALES_FORCE,
        name: (
            <FormattedMessage
                id={`Settings.user.role.${BROWSER_VARIABLE.SALES_FORCE}`}
                defaultMessage="SalesForce"
            />
        ),
    },
    {
        value: BROWSER_VARIABLE.SUCCESS_FACTORS,
        name: (
            <FormattedMessage
                id={`Settings.identity.role.${BROWSER_VARIABLE.SUCCESS_FACTORS}`}
                defaultMessage="Success Factors"
            />
        ),
    },
];

const IDENTITY_ROLE_OPTIONS = [
    {
        value: NOT_SET_VALUE,
        name: <FormattedMessage id="Settings.identity.role.notSet" defaultMessage="(Not set)" />,
    },
    {
        value: BROWSER_VARIABLE.COOKIE,
        name: (
            <FormattedMessage
                id={`Settings.identity.role.${BROWSER_VARIABLE.COOKIE}`}
                defaultMessage="Cookie"
            />
        ),
    },
    {
        value: BROWSER_VARIABLE.LOCAL_STORAGE,
        name: (
            <FormattedMessage
                id={`Settings.identity.role.${BROWSER_VARIABLE.LOCAL_STORAGE}`}
                defaultMessage="Local Storage"
            />
        ),
    },
    {
        value: BROWSER_VARIABLE.JS_VARIABLE,
        name: (
            <FormattedMessage
                id={`Settings.identity.role.${BROWSER_VARIABLE.JS_VARIABLE}`}
                defaultMessage="JavaScript Variable"
            />
        ),
    },
    {
        value: BROWSER_VARIABLE.CSS_PATH,
        name: (
            <FormattedMessage
                id={`Settings.identity.role.${BROWSER_VARIABLE.CSS_PATH}`}
                defaultMessage="CSS Selector"
            />
        ),
    },
    {
        value: BROWSER_VARIABLE.URL_QUERY,
        name: (
            <FormattedMessage
                id={`Settings.identity.role.${BROWSER_VARIABLE.URL_QUERY}`}
                defaultMessage="URL Query"
            />
        ),
    },
];

const Identity = ({ classes, form, form: { getFieldValue }, match }) => {
    const dispatch = useDispatch();

    const { formatMessage } = useIntl();

    const userDefinitionSourceType = useSelector(selectApplicationUserDefinitionSourceType);
    const userDefinitionValuePath = useSelector(selectApplicationUserDefinitionValuePath);
    const roleDefinitionSourceType = useSelector(selectApplicationRoleDefinitionSourceType);
    const roleDefinitionValuePath = useSelector(selectApplicationRoleDefinitionValuePath);

    const handleSaveIdentitySettings = useCallback(() => {
        const { userSource, userPath = '', roleSource, rolePath = '' } = getFieldValue('identity');
        dispatch(
            updateApplicationIdentityRequest(match.params.appId, {
                user: { sourceType: userSource, valuePath: userPath },
                role: { sourceType: roleSource, valuePath: rolePath },
            })
        );
    }, [dispatch, getFieldValue, match.params.appId]);

    const isUserPathDisabled = getFieldValue('identity.userSource')
        ? getFieldValue('identity.userSource') === BROWSER_VARIABLE.SALES_FORCE
        : userDefinitionSourceType === BROWSER_VARIABLE.SALES_FORCE;
    const isRolePathDisabled = getFieldValue('identity.roleSource')
        ? getFieldValue('identity.roleSource') === BROWSER_VARIABLE.SUCCESS_FACTORS
        : roleDefinitionSourceType === BROWSER_VARIABLE.SUCCESS_FACTORS;

    const isUserSourceSelectedValueCookie =
        getFieldValue('identity.userSource') === BROWSER_VARIABLE.COOKIE ||
        userDefinitionSourceType === BROWSER_VARIABLE.COOKIE;
    const isRoleSourceSelectedValueCookie =
        getFieldValue('identity.roleSource') === BROWSER_VARIABLE.COOKIE ||
        roleDefinitionSourceType === BROWSER_VARIABLE.COOKIE;

    return (
        <Grid container className={classes.root}>
            <Grid item xs={12}>
                <Typography variant="subtitle1" className={classes.title}>
                    <FormattedMessage id="Settings.identity.identityAndUserProfile" />
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="body1" className={classes.message}>
                    <FormattedMessage id="Settings.identity.message" />
                </Typography>
            </Grid>
            <Grid item xs={12} container direction="row" alignItems="flex-end" className={classes.items}>
                <Grid item className={classes.formInput}>
                    <FormSelect
                        form={form as any}
                        fullWidth
                        isRequired
                        initialValue={userDefinitionSourceType || NOT_SET_VALUE}
                        label={<FormattedMessage id="Settings.identity.label" />}
                        formItemKey="identity.userSource"
                        optionList={IDENTITY_USER_OPTIONS}
                    />
                </Grid>
                <Grid item>
                    <Typography variant="body1" className={classes.smallText}>
                        <FormattedMessage id="Settings.language.withPath" />
                    </Typography>
                </Grid>
                <Grid item className={classes.formInputLarge}>
                    <FormInput
                        fullWidth
                        form={form}
                        type="text"
                        initialValue={userDefinitionValuePath}
                        formItemKey="identity.userPath"
                        placeholder={formatMessage({ id: 'Settings.identity.label3' })}
                        disabled={isUserPathDisabled}
                        helperText={
                            isUserSourceSelectedValueCookie
                                ? formatMessage({ id: 'Settings.identity.helperText' })
                                : ''
                        }
                    />
                </Grid>
            </Grid>
            <Grid item xs={12} container direction="row" alignItems="flex-end" className={classes.items}>
                <Grid item className={classes.formInput}>
                    <FormSelect
                        form={form as any}
                        fullWidth
                        isRequired
                        initialValue={roleDefinitionSourceType || NOT_SET_VALUE}
                        label={<FormattedMessage id="Settings.identity.label2" />}
                        formItemKey="identity.roleSource"
                        optionList={IDENTITY_ROLE_OPTIONS}
                    />
                </Grid>
                <Grid item>
                    <Typography variant="body1" className={classes.smallText}>
                        <FormattedMessage id="Settings.language.withPath" />
                    </Typography>
                </Grid>
                <Grid item className={classes.formInputLarge}>
                    <FormInput
                        fullWidth
                        form={form}
                        type="text"
                        initialValue={roleDefinitionValuePath}
                        formItemKey="identity.rolePath"
                        placeholder={formatMessage({ id: 'Settings.identity.label3' })}
                        disabled={isRolePathDisabled}
                        helperText={
                            isRoleSourceSelectedValueCookie
                                ? formatMessage({ id: 'Settings.identity.helperText' })
                                : ''
                        }
                    />
                </Grid>
            </Grid>
            <Grid item xs={12} alignItems="center">
                <Typography variant="button">
                    <Button
                        id="yeself-button"
                        variant="contained"
                        color="primary"
                        onClick={handleSaveIdentitySettings}
                        className={classes.btn}
                    >
                        <FormattedMessage id="Buttons.save" />
                    </Button>
                </Typography>
            </Grid>
        </Grid>
    );
};

export default withStyles(styles)(Identity);
